import React, { useEffect, useState } from 'react';
import { Loader2, Trophy, X } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import type { CRMUser, EmployeeAwardDefinition } from '../../types';
import {
  grantEmployeeAward,
  subscribeToAwardDefinitions,
} from '../../utils/employeeAchievements';
import './GrantAchievementModal.css';

interface GrantAchievementModalProps {
  user: CRMUser;
  onClose: () => void;
  onGranted?: () => void;
}

const GrantAchievementModal: React.FC<GrantAchievementModalProps> = ({
  user,
  onClose,
  onGranted,
}) => {
  const { user: authUser } = useAuth();
  const [definitions, setDefinitions] = useState<EmployeeAwardDefinition[]>([]);
  const [awardDefinitionId, setAwardDefinitionId] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeToAwardDefinitions(setDefinitions), []);

  const activeDefs = definitions.filter(d => d.isActive);

  useEffect(() => {
    if (!awardDefinitionId && activeDefs.length > 0) {
      setAwardDefinitionId(activeDefs[0].id ?? '');
    }
  }, [activeDefs, awardDefinitionId]);

  const selected = activeDefs.find(d => d.id === awardDefinitionId);

  const handleGrant = async () => {
    if (!authUser?.id || !awardDefinitionId) return;
    setBusy(true);
    setError(null);
    try {
      await grantEmployeeAward({
        userId: user.uid,
        awardDefinitionId,
        grantedBy: authUser.id,
        grantNote: grantNote.trim() || undefined,
      });
      onGranted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grant failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grant-ach-overlay" onClick={onClose}>
      <div className="grant-ach-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="grant-ach-header">
          <h3>
            <Trophy size={18} aria-hidden /> Award achievement
          </h3>
          <button type="button" className="grant-ach-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="grant-ach-sub">
          Grant a certification or achievement to <strong>{user.displayName}</strong>.
        </p>

        {activeDefs.length === 0 ? (
          <p className="grant-ach-empty">
            No active awards defined yet. Create awards in Settings → Certifications &amp; Achievements.
          </p>
        ) : (
          <>
            <label className="grant-ach-field">
              <span>Award</span>
              <select
                value={awardDefinitionId}
                onChange={e => setAwardDefinitionId(e.target.value)}
                disabled={busy}
              >
                {activeDefs.map(def => (
                  <option key={def.id} value={def.id}>
                    {def.name}
                  </option>
                ))}
              </select>
            </label>

            {selected && (
              <div className="grant-ach-preview">
                <img src={selected.iconUrl} alt="" />
                <div>
                  <div className="grant-ach-preview-name">{selected.name}</div>
                  <div className="grant-ach-preview-desc">{selected.description}</div>
                </div>
              </div>
            )}

            <label className="grant-ach-field">
              <span>Optional note (shown in unlock popup)</span>
              <textarea
                value={grantNote}
                onChange={e => setGrantNote(e.target.value)}
                placeholder="For heroic patience during the beam delivery saga."
                rows={2}
                maxLength={200}
                disabled={busy}
              />
            </label>
          </>
        )}

        {error && <p className="grant-ach-error">{error}</p>}

        <div className="grant-ach-footer">
          <button type="button" className="um-btn-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="um-btn-save"
            onClick={handleGrant}
            disabled={busy || activeDefs.length === 0 || !awardDefinitionId}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="grant-ach-spin" aria-hidden /> Granting…
              </>
            ) : (
              'Grant award'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrantAchievementModal;
